import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import validate from 'validate.js';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import { enqueueSnackbar } from 'actions';
import clsx from 'clsx';
import moment from 'moment';
import { 
	Button, 
	Card, 
	Grid2 as Grid,  // Updated import
	CardHeader, 
	CardContent, 
	Typography,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Divider,
	Chip
} from '@mui/material';
import _ from 'underscore';
import { FilePicker } from 'components';
import DatePicker from 'components/DatePicker/DatePicker';

import { 
	SetFormState, 
	ProcessMocnWork, 
	GenerateAuditReport, 
	ProcessEnodebPairingWork 
} from '../../actions/ScriptsGenerationActions';

const useStyles = makeStyles(theme => ({
	root: {},
	sectionCard: {
		marginBottom: theme.spacing(3)
	},
	sectionTitle: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(2),
		fontWeight: 600
	},
	resultsCard: {
		marginTop: theme.spacing(2)
	},
	statusSuccess: {
		backgroundColor: '#e8f5e8',
		color: '#2e7d32'
	},
	statusFailed: {
		backgroundColor: '#ffebee',
		color: '#c62828'
	},
	tableContainer: {
		maxHeight: 400
	}
}));

const ScriptsGenerationDialogForm = (props) => {
	const { className, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector(state => state.scriptsGenerationReducer.formState);
	const userId = useSelector(state => state.session.user.id);
	const userRole = useSelector(state => state.session.user.role);
	const isLoading = useSelector(state => state.scriptsGenerationReducer.isLoading);
	const isEnodebPairingLoading = useSelector(state => state.scriptsGenerationReducer.isEnodebPairingLoading);
	const processingResult = useSelector(state => state.scriptsGenerationReducer.processingResult);
	const enodebPairingResult = useSelector(state => state.scriptsGenerationReducer.enodebPairingResult);

	const _handleChange = event => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value
			},
			touched: {
				...formState.touched,
				[event.target.name]: true
			}
		});
	};

	const _handleDateChange = (date) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				deploymentDate: date
			},
			touched: {
				...formState.touched,
				deploymentDate: true
			}
		});
	};

	const _onChangeExcelFile = (event) => {
		const acceptedTypes = [
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
			'application/vnd.ms-excel' // .xls
		];

		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			
			if (acceptedTypes.includes(file.type)) {
				setFormState({
					...formState,
					values: {
						...formState.values,
						excelInputFile: file
					}
				});
			} else {
				dispatch(enqueueSnackbar({ 
					message: 'Please select an Excel file (.xlsx or .xls)', 
					options: { variant: 'warning' } 
				}));
			}
		}
	};

	// NEW: EnodebPairing Excel file handler
	const _onChangeEnodebPairingExcelFile = (event) => {
		const acceptedTypes = [
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
			'application/vnd.ms-excel' // .xls
		];

		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			
			if (acceptedTypes.includes(file.type)) {
				setFormState({
					...formState,
					values: {
						...formState.values,
						enodebPairingExcelFile: file
					}
				});
			} else {
				dispatch(enqueueSnackbar({ 
					message: 'Please select an Excel file (.xlsx or .xls)', 
					options: { variant: 'warning' } 
				}));
			}
		}
	};

	const _generateAuditReport = () => {
		dispatch(GenerateAuditReport());
	};

	const _processMocnWork = (event) => {
		// Validate required fields
		if (!formState.values.deploymentDate) {
			dispatch(enqueueSnackbar({ 
				message: 'Deployment date is required', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		if (!formState.values.excelInputFile) {
			dispatch(enqueueSnackbar({ 
				message: 'Please select an Excel file', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		if (formState.values.excelInputFile.size > 50000000) { // 50MB limit for Excel
			dispatch(enqueueSnackbar({ 
				message: 'File size cannot be larger than 50MB', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		// Create the request object with defaults
		const request = {
			deploymentDate: moment(formState.values.deploymentDate).format('YYYY-MM-DDTHH:mm:ss'),
			useDatabase: true, // Default to database mode
			scfFolderPath: "C:\\temp\\" // Default SCF folder path
		};

		dispatch(ProcessMocnWork(request, formState.values.excelInputFile));
	};

	// EnodebPairing processing function
	const _processEnodebPairingWork = (event) => {
		// Validate required fields
		if (!formState.values.deploymentDate) {
			dispatch(enqueueSnackbar({ 
				message: 'Deployment date is required', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		if (!formState.values.enodebPairingExcelFile) {
			dispatch(enqueueSnackbar({ 
				message: 'Please select an Excel file with SiteId and S3EnodebIdPairing columns', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		if (formState.values.enodebPairingExcelFile.size > 50000000) { // 50MB limit
			dispatch(enqueueSnackbar({ 
				message: 'File size cannot be larger than 50MB', 
				options: { variant: 'warning' } 
			}));
			return;
		}

		// Create the request object with defaults
		const request = {
			deploymentDate: moment(formState.values.deploymentDate).format('YYYY-MM-DDTHH:mm:ss'),
			useDatabase: true, // Default to database mode
			scfFolderPath: "C:\\temp\\Scripts\\Output" // Default SCF folder path
		};

		dispatch(ProcessEnodebPairingWork(request, formState.values.enodebPairingExcelFile));
	};

	// Initialize default values if not set
	useEffect(() => {
		if (!formState.values.deploymentDate) {
			setFormState({
				...formState,
				values: {
					...formState.values,
					deploymentDate: new Date()
				}
			});
		}
	}, []);

	// Helper function to render status chip
	const renderStatusChip = (success) => (
		<Chip 
			label={success ? 'Success' : 'Failed'} 
			color={success ? 'success' : 'error'}
			size="small"
			style={{ borderRadius: '4px' }}
		/>
	);

	return (
		<div {...rest} className={clsx(classes.root, className)}>
			{/* Use Grid2 container with proper direction and spacing */}
			<Grid container direction="column" spacing={3}>
				{/* MOCN Scripts Processing Section */}
				<Grid>
					<Card className={classes.sectionCard} style={{ maxWidth: '790px' }}>
						<CardHeader title="MOCN Scripts Processing" />
						<CardContent>
							<Grid container spacing={2}>
								{/* Deployment Date */}
								<Grid size={{ xs: 12, md: 6 }}>
									<DatePicker
										label="Deployment Date"
										name="deploymentDate"
										value={formState.values.deploymentDate || new Date()}
										onChange={_handleDateChange}
										format="YYYY/MM/DD"
										disabled={isLoading}
									/>
								</Grid>

								{/* Excel Input File */}
								<Grid size={{ xs: 12, md: 6 }}>
									<FilePicker
										placeholder="Select Excel file with site information"
										accept=".xlsx,.xls"
										onChange={_onChangeExcelFile}
										value={formState.values.excelInputFile?.name || ''}
										disabled={isLoading}
									/>
								</Grid>

								{/* Process Button */}
								<Grid size={12}>
									<Button
										onClick={_processMocnWork}
										color="primary"
										size="large"
										variant="contained"
										disabled={
											isLoading || 
											!formState.values.deploymentDate || 
											!formState.values.excelInputFile
										}
										fullWidth
									>
										{isLoading ? 'Processing...' : 'Process MOCN Work'}
									</Button>
								</Grid>
							</Grid>

							{/* MOCN Results Display */}
							{processingResult && !processingResult.auditSummary && (
								<>
									<Card variant="outlined" className={classes.resultsCard}>
										<CardContent>
											<Typography variant="h6" gutterBottom>
												MOCN Processing Results
											</Typography>
											<Grid container spacing={2}>
												<Grid size={{ xs: 6, sm: 3 }}>
													<Typography variant="body2" color="textSecondary">
														Total Sites
													</Typography>
													<Typography variant="h6">
														{processingResult.totalSites || 0}
													</Typography>
												</Grid>
												<Grid size={{ xs: 6, sm: 3 }}>
													<Typography variant="body2" color="textSecondary">
														Processed Files
													</Typography>
													<Typography variant="h6" style={{ color: '#2e7d32' }}>
														{processingResult.processedSites || 0}
													</Typography>
												</Grid>
												<Grid size={{ xs: 6, sm: 3 }}>
													<Typography variant="body2" color="textSecondary">
														Failed Files
													</Typography>
													<Typography variant="h6" style={{ color: '#c62828' }}>
														{processingResult.failedSites || 0}
													</Typography>
												</Grid>
												<Grid size={{ xs: 6, sm: 3 }}>
													<Typography variant="body2" color="textSecondary">
														Status
													</Typography>
													<Typography variant="h6">
														{renderStatusChip(processingResult.success)}
													</Typography>
												</Grid>
											</Grid>

											{processingResult.error && (
												<Typography variant="body2" color="error" style={{ marginTop: 16 }}>
													<strong>Error:</strong> {processingResult.error}
												</Typography>
											)}
										</CardContent>
									</Card>

									{/* Detailed Site Results (Collapsible) */}
									{processingResult.siteResults && processingResult.siteResults.length > 0 && (
										<Card variant="outlined" className={classes.resultsCard} style={{ marginTop: 16 }}>
											<CardContent>
												<Typography variant="h6" gutterBottom>
													Detailed Site Results
												</Typography>
												<div style={{ maxHeight: 400, overflowY: 'auto' }}>
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell><strong>Site ID</strong></TableCell>
																<TableCell><strong>Status</strong></TableCell>
																<TableCell><strong>Templates</strong></TableCell>
																<TableCell><strong>Files/Error</strong></TableCell>
															</TableRow>
														</TableHead>
														<TableBody>
															{processingResult.siteResults.map((site, index) => (
																<TableRow key={index}>
																	<TableCell>{site.siteId}</TableCell>
																	<TableCell>
																		{site.success ? (
																			<Chip label="✓ OK" size="small" style={{ backgroundColor: '#4caf50', color: 'white' }} />
																		) : (
																			<Chip label="✗ FAIL" size="small" style={{ backgroundColor: '#f44336', color: 'white' }} />
																		)}
																	</TableCell>
																	<TableCell>
																		{site.processedTemplates && site.processedTemplates.length > 0
																			? site.processedTemplates.join(', ')
																			: '-'}
																	</TableCell>
																	<TableCell>
																		{site.success ? (
																			<Typography variant="body2" color="textSecondary">
																				{site.generatedFiles?.length || 0} file(s)
																			</Typography>
																		) : (
																			<Typography variant="body2" color="error" style={{ fontSize: '0.75rem' }}>
																				{site.errorMessage}
																			</Typography>
																		)}
																	</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												</div>
											</CardContent>
										</Card>
									)}
								</>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* Audit Report Section */}
				<Grid>
					<Card className={classes.sectionCard} style={{ maxWidth: '790px' }}>
						<CardHeader title="Audit Report Generation" />
						<CardContent>
							<Grid container spacing={2}>
								<Grid size={12}>
									<Typography variant="body2" color="textSecondary" gutterBottom>
										Generate audit report comparing current dump with previous week's scripts
									</Typography>
									<Button
										onClick={_generateAuditReport}
										color="secondary"
										size="large"
										variant="contained"
										disabled={isLoading}
										fullWidth
									>
										{isLoading ? 'Generating...' : 'Generate Audit Report'}
									</Button>
								</Grid>
							</Grid>

							{/* Audit Results Display */}
							{processingResult && processingResult.auditSummary && (
								<Card variant="outlined" className={classes.resultsCard}>
									<CardContent>
										<Typography variant="h6" gutterBottom>
											Audit Report Results
										</Typography>
										<Grid container spacing={2}>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Total Sites
												</Typography>
												<Typography variant="h6">
													{processingResult.totalSites || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Processed Files
												</Typography>
												<Typography variant="h6" style={{ color: '#2e7d32' }}>
													{processingResult.processedSites || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Failed Files
												</Typography>
												<Typography variant="h6" style={{ color: '#c62828' }}>
													{processingResult.failedSites || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Status
												</Typography>
												<Typography variant="h6">
													{renderStatusChip(processingResult.success)}
												</Typography>
											</Grid>
										</Grid>

										{processingResult.errorMessage && (
											<Typography variant="body2" color="error" style={{ marginTop: 16 }}>
												<strong>Error:</strong> {processingResult.errorMessage}
											</Typography>
										)}
									</CardContent>
								</Card>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* EnodebPairing Processing Section */}
				<Grid>
					<Card className={classes.sectionCard} style={{ maxWidth: '790px' }}>
						<CardHeader title="EnodebPairing Processing" />
						<CardContent>
							<Grid container spacing={2}>
								{/* EnodebPairing Excel File */}
								<Grid size={12}>
									<FilePicker
										placeholder="Select Excel file with SiteId and S3EnodebIdPairing columns"
										accept=".xlsx,.xls"
										onChange={_onChangeEnodebPairingExcelFile}
										value={formState.values.enodebPairingExcelFile?.name || ''}
										disabled={isLoading}
										helperText="Excel file should contain: SiteId, S3EnodebIdPairing columns"
									/>
								</Grid>

								{/* EnodebPairing Process Button */}
								<Grid size={12}>
									<Button
										onClick={_processEnodebPairingWork}
										color="primary"
										size="large"
										variant="contained"
										disabled={
											isEnodebPairingLoading || 
											!formState.values.deploymentDate || 
											!formState.values.enodebPairingExcelFile
										}
										fullWidth
									>
										{isEnodebPairingLoading ? 'Processing...' : 'Process EnodebPairing Work'}
									</Button>
								</Grid>
							</Grid>

							{/* EnodebPairing Results Display */}
							{enodebPairingResult && (
								<Card variant="outlined" className={classes.resultsCard}>
									<CardContent>
										<Typography variant="h6" gutterBottom>
											EnodebPairing Processing Results
										</Typography>
										<Grid container spacing={2}>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Total Sites
												</Typography>
												<Typography variant="h6">
													{enodebPairingResult.totalSites || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Processed Files
												</Typography>
												<Typography variant="h6" style={{ color: '#2e7d32' }}>
													{enodebPairingResult.processedSites || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Failed Files
												</Typography>
												<Typography variant="h6" style={{ color: '#c62828' }}>
													{enodebPairingResult.failedSites || 0}
												</Typography>
											</Grid>
											<Grid size={{ xs: 6, sm: 3 }}>
												<Typography variant="body2" color="textSecondary">
													Status
												</Typography>
												<Typography variant="h6">
													{renderStatusChip(enodebPairingResult.success)}
												</Typography>
											</Grid>
										</Grid>

										{enodebPairingResult.errorMessage && (
											<Typography variant="body2" color="error" style={{ marginTop: 16 }}>
												<strong>Error:</strong> {enodebPairingResult.errorMessage}
											</Typography>
										)}
									</CardContent>
								</Card>
							)}
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</div>
	);
};

ScriptsGenerationDialogForm.propTypes = {
	className: PropTypes.string
};

export default ScriptsGenerationDialogForm;